import React from 'react';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import clsx from 'clsx';
import { Button } from '@components/main-components/button/Button';
import { PlayIndicatorBars } from '@components/main-components/cards/PlayIndicatorBars';
import { useSongCardContext } from './Context';

export function CardButton({
  href,
  label,
  variant,
  onClick,
  trailingIcon,
  disabled,
}: {
  href?: string;
  label: string;
  onClick?: () => void;
  variant: ComponentPropsWithoutRef<typeof Button>['variant'];
  trailingIcon?: ReactNode;
  disabled?: boolean;
}) {
  return (
    <div className="p-2 pt-0">
      <Button
        className="focus:border-none focus:outline-none"
        variant={variant}
        label={label}
        href={href}
        size="M"
        fullWidth
        onClick={onClick}
        trailingIcon={trailingIcon}
        disabled={disabled}
      />
    </div>
  );
}

export function CardInfo({
  subheader,
  header,
  actions,
  bottom,
  infoContainerBottom,
}: {
  /**
   * Name of the artist can go here
   */
  subheader: ReactNode;
  /**
   * Name of the release can go here. React node so that we can pass buttons, links etc.
   */
  header: ReactNode;
  /**
   * To show action buttons on the card
   */
  actions: ReactNode;
  /**
   * To show content at the bottom of the card
   */
  bottom?: ReactNode;
  /**
   * To show content inside the bottom info container
   */
  infoContainerBottom?: ReactNode;
}) {
  const { isPlaying, expanded, edition } = useSongCardContext();

  return (
    <div
      className={clsx(
        `pointer-events-all flex w-full flex-col transition-all`,
        expanded ? 'z-above2 h-full pt-2' : edition ? `h-20` : 'h-auto',
        !!edition && 'absolute bottom-0 bg-black',
      )}
    >
      <div
        className={clsx(
          'flex flex-shrink-0 flex-row justify-between gap-2 bg-black px-3 pb-2 pt-1 group-hover:gap-4 md:gap-0 md:pb-3',
          !!infoContainerBottom ? 'h-20' : 'h-fit',
        )}
      >
        <div className="flex w-full min-w-0 flex-col">
          <div
            className="text-overflow-ellipsis flex flex-shrink-0 overflow-hidden whitespace-nowrap 
          text-base-s text-base400 transition-colors hover:text-white"
          >
            {subheader}
          </div>
          <div className="mt-1 flex items-center gap-1">
            {isPlaying && <PlayIndicatorBars />}
            <div className="overflow-hidden text-ellipsis whitespace-nowrap font-title text-title-s font-medium text-base300 hover:text-white md:text-title-m">
              {header}
            </div>
          </div>
          {infoContainerBottom && (
            <div className="mt-1 flex-grow text-base-s text-base400">{infoContainerBottom}</div>
          )}
        </div>
        <div className="opacity-1 mr-1 flex w-fit min-w-fit flex-shrink-0 flex-col justify-end group-hover:w-fit group-hover:min-w-fit group-hover:opacity-100 md:w-0 md:min-w-0 md:opacity-0">
          {actions}
        </div>
      </div>
      {bottom}
    </div>
  );
}

export const CARD_INFO_HEIGHT = 64;

export const CARD_INFO_HEIGHT_WITH_EDITIONS = CARD_INFO_HEIGHT + 24;

CardInfo.Button = CardButton;
